import { Activity, CheckCircle2, RefreshCw, XCircle } from 'lucide-react';

const valueText = (value) => typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value ?? '—');

export const VoiceDiagnosticsPanel = ({ open, onClose, diagnostics, environment, events, features, onRetry, orchestration = {} }) => {
  if (!open) return null;
  const metrics = [
    ['Permission', diagnostics.permission],
    ['Stream active', diagnostics.streamActive],
    ['Track ready', diagnostics.trackReadyState],
    ['Track enabled', diagnostics.trackEnabled],
    ['Track muted', diagnostics.trackMuted],
    ['Recorder', diagnostics.recorderState],
    ['Audio context', diagnostics.audioContextState],
    ['RMS', Number(diagnostics.rms || 0).toFixed(4)],
    ['Peak', Number(diagnostics.peak || 0).toFixed(4)],
    ['FFT energy', Number(diagnostics.fftEnergy || 0).toFixed(4)],
    ['Blob size', `${diagnostics.blobSize || 0} bytes`],
    ['Blob duration', `${diagnostics.blobDuration || 0} ms`],
    ['Upload', diagnostics.uploadStatus],
    ['Speech-to-text', diagnostics.sttStatus],
    ['AI response', diagnostics.llmStatus],
    ['Voice generation', diagnostics.ttsStatus],
    ['Playback', diagnostics.playbackStatus],
    ['Codec', diagnostics.codec],
    ['Sample rate', `${diagnostics.sampleRate || 0} Hz`],
    ['Conversation ID', orchestration.conversationId || '—'],
    ['Device model', environment.deviceModel || 'Unknown'],
    ['Turn ID', orchestration.turnId || '—'],
    ['Turn number', orchestration.turnNumber || 0],
    ['Current state', orchestration.state || 'IDLE'],
    ['Microphone status', diagnostics.microphoneStatus || 'idle'],
    ['Active streams', diagnostics.activeStreams || 0],
    ['Recovery attempts', diagnostics.recoveryAttempts || orchestration.recoveryAttempts || 0],
    ['Turn confidence', `${Math.round((diagnostics.turnConfidence || 0) * 100)}%`],
    ['Adaptive silence', diagnostics.adaptiveSilenceMs ? `${diagnostics.adaptiveSilenceMs} ms` : '—'],
    ['Pause history', diagnostics.pauseCount || 0],
    ['Speaking rate', diagnostics.speakingRateWpm ? `${diagnostics.speakingRateWpm} wpm` : 'Learning'],
    ['Semantic signal', diagnostics.semanticStatus || 'unknown'],
    ['Turn decision', diagnostics.turnDecision || 'listening'],
    ['TTS queue', orchestration.ttsQueue || 0],
    ['Playback queue', orchestration.playbackQueue || 0],
    ['Chunks received', orchestration.chunksReceived || 0],
    ['Chunks played', orchestration.chunksPlayed || 0],
    ['Chunk order', orchestration.chunkOrder?.join(' → ') || '—'],
    ['Memory used', orchestration.memoryUsage?.usedJSHeapSize ? `${Math.round(orchestration.memoryUsage.usedJSHeapSize / 1048576)} MB` : 'Unavailable'],
    ['Measured memory growth', orchestration.memorySamples?.length > 1 ? `${(orchestration.memoryGrowth / 1048576).toFixed(1)} MB across ${orchestration.memorySamples.length} turns` : 'Awaiting samples'],
    ['Last error code', diagnostics.lastErrorCode || '—'],
    ['Self tests', orchestration.selfTests ? (orchestration.selfTests.passed ? 'Passed' : 'Failed') : 'Not run'],
  ];

  return (
    <div className="fixed inset-0 z-[70] bg-black/35 backdrop-blur-sm p-3 sm:p-6" data-testid="voice-diagnostics-overlay">
      <section className="ml-auto h-full w-full max-w-xl overflow-y-auto bg-[#FAF8F5] shadow-2xl" data-testid="voice-diagnostics-panel">
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-black/10 bg-[#FAF8F5]/95 px-5 py-4 backdrop-blur">
          <div className="flex items-center gap-3">
            <Activity className="h-5 w-5 text-[#6B8F71]" />
            <div>
              <h2 className="text-lg font-semibold text-[#2D2A26]">Voice diagnostics</h2>
              <p className="text-xs text-[#6B6560]" data-testid="voice-diagnostics-environment">{environment.browser} · {environment.os} · {environment.device}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-[#6B6560] hover:bg-black/5" aria-label="Close voice diagnostics" data-testid="voice-diagnostics-close-button">
            <XCircle className="h-5 w-5" />
          </button>
        </header>

        <div className="space-y-7 p-5">
          {diagnostics.lastError && (
            <div className="border-l-4 border-[#C4613A] bg-[#C4613A]/5 px-4 py-3 text-sm text-[#7A3824]" data-testid="voice-diagnostics-error">
              {diagnostics.lastError}
            </div>
          )}

          <section>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase text-[#8B7355]">Live pipeline</h3>
              <button onClick={onRetry} className="flex items-center gap-1.5 text-xs font-semibold text-[#6B8F71]" data-testid="voice-diagnostics-retry-button">
                <RefreshCw className="h-3.5 w-3.5" /> Reconnect
              </button>
            </div>
            <dl className="grid grid-cols-2 gap-x-5 border-y border-black/10">
              {metrics.map(([label, value]) => (
                <div className="flex min-w-0 flex-col gap-1 border-b border-black/5 py-2.5" key={label} data-testid={`voice-diagnostic-${label.toLowerCase().replaceAll(' ', '-')}`}>
                  <dt className="text-[11px] uppercase text-[#A39E98]">{label}</dt>
                  <dd className="break-all text-sm font-medium text-[#2D2A26]">{valueText(value)}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase text-[#8B7355]">API support</h3>
            <div className="grid grid-cols-2 gap-2" data-testid="voice-feature-matrix">
              {Object.entries(features).map(([name, supported]) => (
                <div className="flex items-center gap-2 text-xs text-[#5A5040]" key={name}>
                  <CheckCircle2 className={`h-3.5 w-3.5 ${supported ? 'text-[#6B8F71]' : 'text-[#C4613A]'}`} />
                  <span className="break-all">{name}: {supported ? 'available' : 'unavailable'}</span>
                </div>
              ))}
            </div>
          </section>

          {orchestration.selfTests?.results?.length > 0 && (
            <section data-testid="voice-self-test-results">
              <h3 className="mb-3 text-xs font-semibold uppercase text-[#8B7355]">Deterministic self tests</h3>
              <div className="border-y border-black/10">
                {orchestration.selfTests.results.map((result, index) => (
                  <div
                    className="flex items-center justify-between gap-4 border-b border-black/5 py-2.5"
                    key={result.name}
                    data-testid={`voice-self-test-${index}`}
                  >
                    <span className="text-xs text-[#5A5040]">{result.name}</span>
                    <span className={`flex shrink-0 items-center gap-1 text-xs font-semibold ${result.passed ? 'text-[#6B8F71]' : 'text-[#C4613A]'}`}>
                      {result.passed ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                      {result.passed ? 'Passed' : 'Failed'}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section data-testid="voice-latency-waterfall">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-xs font-semibold uppercase text-[#8B7355]">Turn latency waterfall</h3>
                <span className={`text-xs font-semibold ${orchestration.latencySummary?.regression ? 'text-[#C4613A]' : 'text-[#6B8F71]'}`} data-testid="voice-technical-latency">
                  {orchestration.latencySummary?.technicalLatencyMs == null ? 'In progress' : `${orchestration.latencySummary.technicalLatencyMs} ms to speech`}
                </span>
              </div>
              <div className="border-y border-black/10">
                {!orchestration.latencyWaterfall?.length && (
                  <div className="flex items-center justify-between gap-3 py-3 text-xs" data-testid="latency-stage-awaiting-speech-end">
                    <span className="text-[#5A5040]">{orchestration.latencyTurn ? 'Awaiting end of speech' : 'Awaiting user turn'}</span>
                    <span className="font-medium text-[#8B7355]">Measuring</span>
                  </div>
                )}
                {(orchestration.latencyWaterfall || []).map((row) => (
                  <div className="grid grid-cols-[1fr_auto_auto] gap-3 border-b border-black/5 py-2 text-xs" key={row.stage} data-testid={`latency-stage-${row.stage}`}>
                    <span className="text-[#5A5040]">{row.stage.replaceAll('_', ' ')}</span>
                    <span className="font-medium text-[#2D2A26]">+{row.atMs} ms</span>
                    <span className="text-[#8A8580]">{row.durationMs} ms</span>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-[#8A8580]" data-testid="voice-perceived-latency">
                Visible acknowledgement: {orchestration.latencySummary?.perceivedLatencyMs == null ? 'pending' : `${orchestration.latencySummary.perceivedLatencyMs} ms`}
              </p>
          </section>

          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase text-[#8B7355]">Recent events</h3>
            <div className="max-h-80 overflow-y-auto border-y border-black/10" data-testid="voice-event-log">
              {[...events].reverse().map((event, index) => (
                <div className="border-b border-black/5 py-2.5" key={`${event.timestamp}-${index}`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-semibold text-[#2D2A26]">{event.stage}</span>
                    <span className="text-[10px] text-[#A39E98]">{new Date(event.timestamp).toLocaleTimeString()}</span>
                  </div>
                  <p className="text-xs text-[#6B6560]">{event.status}{event.error ? ` · ${event.error}` : ''}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </section>
    </div>
  );
};
